import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { trackEvent } from "@/lib/analytics";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/la-cucina", label: "La cucina" },
  { to: "/menu", label: "Menu" },
  { to: "/il-borgo", label: "Il borgo" },
  { to: "/progetti-futuri", label: "Progetti futuri" },
  { to: "/diario-dal-borgo", label: "Diario" },
  { to: "/galleria", label: "Galleria" },
  { to: "/contatti", label: "Contatti" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const transparent = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,padding] duration-700 ${
          transparent
            ? "bg-gradient-to-b from-wood-deep/85 via-wood-deep/45 to-transparent border-b border-transparent py-5"
            : "bg-cream/95 backdrop-blur-md border-b border-border py-3"
        }`}
      >
        <div className="container-ed flex items-center justify-between gap-3 xl:gap-6">
          <Link
            to="/"
            data-testid="logo-link"
            aria-label="Osteria Aterrana, home"
            className="flex shrink-0 items-center gap-3"
          >
            <img
              src="/logo-osteria-aterrana.png"
              alt="Osteria Aterrana"
              className={`h-11 w-auto lg:h-10 xl:h-12 2xl:h-14 transition-[filter,opacity] duration-500 ${
                transparent ? "brightness-0 invert opacity-90" : ""
              }`}
            />
            <span className="sr-only">Osteria Aterrana · Aterrana, Montoro</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8" aria-label="Navigazione principale">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline whitespace-nowrap text-[0.62rem] xl:text-[0.68rem] 2xl:text-[0.72rem] uppercase tracking-[0.12em] xl:tracking-[0.16em] 2xl:tracking-[0.2em] transition-colors duration-400 ${
                    transparent
                      ? "text-cream/85 hover:text-cream"
                      : isActive
                        ? "text-sage"
                        : "text-wood/80 hover:text-sage"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${settings.phone_link || "+393311091971"}`}
              data-testid="header-phone"
              onClick={() => trackEvent("click_phone", { position: "header" })}
              className={`hidden xl:flex items-center gap-2 whitespace-nowrap text-[0.68rem] tracking-wider ${
                transparent ? "text-cream/70" : "text-wood/60"
              } hover:text-sage transition-colors duration-400`}
            >
              <Phone size={13} strokeWidth={1.5} /> {settings.phone || "331 10 91 971"}
            </a>
            <Link
              to="/prenota"
              data-testid="nav-prenota"
              onClick={() => trackEvent("click_booking", { position: "header" })}
              className={`shrink-0 whitespace-nowrap px-4 py-2.5 xl:px-6 xl:py-3 text-[0.62rem] xl:text-[0.68rem] uppercase tracking-[0.16em] xl:tracking-[0.2em] transition-colors duration-500 ${
                transparent
                  ? "bg-cream text-wood hover:bg-gold"
                  : "bg-sage text-cream hover:bg-wood"
              }`}
            >
              Prenota
            </Link>
          </nav>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Apri il menu"
            className={`lg:hidden p-2 -mr-2 ${transparent ? "text-cream" : "text-wood"}`}
          >
            <Menu size={24} strokeWidth={1.3} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-wood-deep grain flex flex-col"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            role="dialog"
            aria-modal="true"
            aria-label="Menu di navigazione"
          >
            <div className="container-ed flex items-center justify-between py-5">
              <img
                src="/logo-osteria-aterrana.png"
                alt="Osteria Aterrana"
                className="h-11 w-auto brightness-0 invert opacity-90"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Chiudi il menu"
                className="p-2 -mr-2 text-cream"
              >
                <X size={24} strokeWidth={1.3} />
              </button>
            </div>
            <nav className="container-ed flex-1 flex flex-col justify-center gap-2" aria-label="Navigazione mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block font-serif text-4xl text-cream/90 py-2.5 border-b border-cream/10"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="container-ed pb-10 space-y-4">
              <Link
                to="/prenota"
                data-testid="mobile-nav-prenota"
                className="flex items-center justify-center bg-gold text-wood-deep py-4 text-[0.72rem] uppercase tracking-[0.22em]"
              >
                Prenota un tavolo
              </Link>
              <a
                href={`tel:${settings.phone_link || "+393311091971"}`}
                className="flex items-center justify-center gap-2 border border-cream/30 text-cream py-4 text-[0.72rem] uppercase tracking-[0.2em]"
              >
                <Phone size={14} strokeWidth={1.5} /> {settings.phone || "331 10 91 971"}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
